import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { friendlyApiErrorMessage } from "src/app/services/api-error-notifier.service";
import { SmMessageRoomResponse, SmPortalMessage, SmResolveResponse } from "./secure-message.model";
import { SecureMessageRealtimeService, SmRealtimeEvent } from "./secure-message-realtime.service";
import { SecureMessageService } from "./secure-message.service";

type SmPageStage = "loading" | "expired" | "verify" | "room" | "session-expired";

const IDLE_MS = 20 * 60 * 1000;
const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;
const ALLOWED_EXT = new Set(["pdf", "jpg", "jpeg", "png"]);

@Component({
  selector: "app-secure-message-page",
  templateUrl: "./secure-message-page.component.html",
  styleUrls: ["./secure-message-page.component.scss"],
  standalone: false,
})
export class SecureMessagePageComponent implements OnInit, OnDestroy {
  @ViewChild("threadScroll") threadScroll?: ElementRef<HTMLElement>;

  token = "";
  stage: SmPageStage = "loading";
  error = "";
  info = "";
  resolve: SmResolveResponse | null = null;

  workEmail = "";
  otpCode = "";
  otpSent = false;
  otpHint = "Sent to your work email";
  sessionToken = "";

  room: SmMessageRoomResponse | null = null;
  replyBody = "";
  quickAnswerCd = "";
  files: File[] = [];
  attachmentError = "";
  busy = false;
  replySent = false;

  private destroy$ = new Subject<void>();
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private hubJoined = false;

  constructor(
    private route: ActivatedRoute,
    private api: SecureMessageService,
    private realtime: SecureMessageRealtimeService
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get("token") ?? "";
    if (!this.token.trim()) {
      this.stage = "expired";
      return;
    }
    this.realtime
      .onMessage()
      .pipe(takeUntil(this.destroy$))
      .subscribe((ev) => this.onRealtimeMessage(ev));
    this.loadResolve();
  }

  ngOnDestroy(): void {
    this.clearIdleTimer();
    void this.realtime.stop();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener("document:keydown")
  @HostListener("document:click")
  @HostListener("document:mousemove")
  onActivity(): void {
    if (this.stage === "room" && this.sessionToken) this.resetIdleTimer();
  }

  get canSendOtp(): boolean {
    return this.workEmail.trim().includes("@") && !this.busy;
  }

  get canVerify(): boolean {
    return this.canSendOtp && /^\d{6}$/.test(this.otpCode.trim()) && !this.busy;
  }

  get canReply(): boolean {
    return (!!this.replyBody.trim() || !!this.quickAnswerCd) && !this.busy && !this.attachmentError;
  }

  requestNewLink(): void {
    this.busy = true;
    this.error = "";
    this.api
      .requestNewLink({ token: this.token })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.busy = false;
          this.info = res.message ?? "A new link was sent to the named recipient's work email.";
        },
        error: (err) => {
          this.busy = false;
          this.error = friendlyApiErrorMessage(err);
        },
      });
  }

  sendOtp(): void {
    if (!this.canSendOtp) return;
    this.busy = true;
    this.error = "";
    this.api
      .sendOtp({ token: this.token, work_email: this.workEmail.trim() })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.busy = false;
          this.otpSent = true;
          this.otpHint = "Sent to your work email";
        },
        error: (err) => {
          this.busy = false;
          this.error = friendlyApiErrorMessage(err);
        },
      });
  }

  verifyOtp(): void {
    if (!this.canVerify) return;
    this.busy = true;
    this.error = "";
    this.api
      .verifyOtp({ token: this.token, work_email: this.workEmail.trim(), code: this.otpCode.trim() })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.busy = false;
          if (!res.ok || !res.session_token) {
            this.error = res.message ?? "That code did not work. Try again or request a new code.";
            return;
          }
          this.sessionToken = res.session_token;
          this.loadRoom();
        },
        error: (err) => {
          this.busy = false;
          this.error = friendlyApiErrorMessage(err);
        },
      });
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const list = input.files ? Array.from(input.files) : [];
    this.attachmentError = "";
    const accepted: File[] = [];
    for (const f of list) {
      const ext = f.name.split(".").pop()?.toLowerCase() ?? "";
      if (!ALLOWED_EXT.has(ext)) {
        this.attachmentError = "Attachments must be PDF, JPG, or PNG.";
        continue;
      }
      if (f.size > MAX_ATTACHMENT_BYTES) {
        this.attachmentError = "Each attachment must be 20 MB or less.";
        continue;
      }
      accepted.push(f);
    }
    this.files = accepted;
    input.value = "";
  }

  removeFile(index: number): void {
    this.files = this.files.filter((_, i) => i !== index);
    this.attachmentError = "";
  }

  pickQuick(code: string): void {
    this.quickAnswerCd = this.quickAnswerCd === code ? "" : code;
  }

  sendReply(): void {
    if (!this.canReply || !this.sessionToken) return;
    this.busy = true;
    this.error = "";
    this.api
      .reply({
        session_token: this.sessionToken,
        body_txt: this.replyBody.trim(),
        quick_answer_cd: this.quickAnswerCd || undefined,
        attachment_nms: this.files.map((f) => f.name),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (room) => {
          this.busy = false;
          this.room = room;
          this.replySent = true;
          this.replyBody = "";
          this.quickAnswerCd = "";
          this.files = [];
          this.info = "Your reply stays in this secure area. It is not sent by email.";
          this.resetIdleTimer();
          this.scrollThreadToEnd();
        },
        error: (err) => {
          this.busy = false;
          this.error = friendlyApiErrorMessage(err);
        },
      });
  }

  backToVerify(): void {
    this.clearSession();
    this.stage = "verify";
  }

  private loadResolve(): void {
    this.stage = "loading";
    this.api
      .resolve({ token: this.token })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.resolve = res;
          if (res.status_cd === "EXPIRED" || res.status_cd === "INVALID" || res.status_cd === "REVOKED") {
            this.stage = "expired";
            this.info = res.message ?? "";
            return;
          }
          this.stage = "verify";
        },
        error: (err) => {
          this.error = friendlyApiErrorMessage(err);
          this.stage = "expired";
        },
      });
  }

  private loadRoom(): void {
    if (!this.sessionToken) return;
    this.busy = true;
    this.api
      .message(this.sessionToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (room) => {
          this.busy = false;
          this.room = {
            ...room,
            messages: room.messages ?? [],
          };
          this.stage = "room";
          this.resetIdleTimer();
          void this.startRealtime();
          setTimeout(() => this.scrollThreadToEnd(), 0);
        },
        error: (err) => {
          this.busy = false;
          this.error = friendlyApiErrorMessage(err);
          this.clearSession();
          this.stage = "session-expired";
        },
      });
  }

  private async startRealtime(): Promise<void> {
    if (!this.sessionToken || this.hubJoined) return;
    try {
      await this.realtime.connectAnonymous();
      await this.realtime.joinBySession(this.sessionToken);
      this.hubJoined = true;
    } catch (err) {
      console.warn("SecureMessageHub practice join failed", err);
    }
  }

  private onRealtimeMessage(ev: SmRealtimeEvent): void {
    if (!this.room || !ev?.message) return;
    const mapped: SmPortalMessage = {
      message_key: ev.message.message_key,
      direction_cd: ev.message.direction_cd,
      author_nm: ev.message.author_nm,
      initials: ev.message.initials,
      // Practice: host messages are not mine.
      is_mine: !ev.is_host,
      body_txt: ev.message.body_txt,
      ts: ev.message.ts,
      attachment_nms: ev.message.attachment_nms || [],
    };
    if (this.room.messages.some((m) => m.message_key === mapped.message_key)) return;
    this.room = { ...this.room, messages: [...this.room.messages, mapped] };
    this.scrollThreadToEnd();
  }

  private scrollThreadToEnd(): void {
    const el = this.threadScroll?.nativeElement;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }

  private resetIdleTimer(): void {
    this.clearIdleTimer();
    this.idleTimer = setTimeout(() => this.onIdleTimeout(), IDLE_MS);
  }

  private clearIdleTimer(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private onIdleTimeout(): void {
    this.clearSession();
    this.stage = "session-expired";
    this.info = "Your session ended after 20 minutes of inactivity. Verify again to continue.";
  }

  private clearSession(): void {
    this.clearIdleTimer();
    void this.realtime.stop();
    this.hubJoined = false;
    this.sessionToken = "";
    this.room = null;
    this.otpCode = "";
    this.otpSent = false;
    this.replySent = false;
  }
}
